import React, { useState, useEffect, useRef } from 'react';
import { useAppData } from './context/DataContext';
import axios from 'axios';
import { ReactComponent as GoogleLogo } from './GoogleLogo.svg';
import ForgotPassword from "./ForgotPassword.js";
import { Modal, Alert, Divider, ConfigProvider, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
    getBaseData,
    getCurrentUser,
    getOrganizationInfo,
} from "./information.service.js";

const secrets = require('./secrets.js');
const api = secrets.server_url;

function Signin(props) {

    const {
        loggedInEmail,
        users,
        setAppData,
    } = useAppData();

    const navigate = useNavigate();

    const [email, setEmail] = useState('');
    const [liPassword, setLiPassword] = useState('');
    const [buttonSignUp, setButtonSignUp] = useState(false);
    const [error, setError] = useState('');

    //need to toggle this to test this feature.
    const [isRegistered, setIsRegistered] = useState(true);

    const [fullName, setFullName] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const [passwordMatchError, setPasswordMatchError] = useState('');
    const [emailError, setEmailError] = useState('');
    const fullNameInputRef = useRef(null); // Reference to the password input
    const [forgotPassword, setForgotPassword] = useState(false);

    const [isModalVisible, setIsModalVisible] = useState(false);

    const [emails, setEmails] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (props.trigger) {
            // Disable scrolling when the modal is open
            document.body.style.overflow = 'hidden';
        } else {
            // Enable scrolling when the modal is closed
            document.body.style.overflow = '';
        }

        // Cleanup function to ensure scrolling is enabled when component unmounts
        return () => {
            document.body.style.overflow = '';
        };
    }, [props.trigger]);

    useEffect(() => {
        // Replace the email fetching with context data
        if (users) {
            const emailList = users.map(user => user.email);
            setEmails(emailList);
            setIsLoading(false);
        }
    }, [users]);

    /** After password login or signup, refresh DataContext from the server session (no full reload). */
    async function syncSessionFromServer() {
        try {
            const baseData = await getBaseData();
            let userInfo = null;
            let orgInfo = {
                selectedOrganization: "",
                selectedOrganizationInfo: null,
                managedOrganizations: [],
            };

            if (baseData.defaultOrg && baseData.defaultOrg.data) {
                orgInfo.selectedOrganization = baseData.defaultOrg.data;
            }

            if (baseData.loggedInEmail && baseData.loggedInEmail.data) {
                userInfo = await getCurrentUser(baseData.loggedInEmail.data);
                if (userInfo && userInfo._id) {
                    const userOrgInfo = await getOrganizationInfo(
                        userInfo._id,
                        baseData.loggedInEmail.data,
                        baseData.defaultOrg?.data
                    );
                    orgInfo = { ...orgInfo, ...userOrgInfo };
                }
            }

            setAppData((prev) => ({
                ...prev,
                ...baseData,
                userInfo,
                ...orgInfo,
            }));

            if (baseData.loggedInEmail?.data && userInfo) {
                try {
                    localStorage.setItem("currentUser", JSON.stringify(userInfo));
                } catch (e) {
                    /* ignore quota / private mode */
                }
            }

            return Boolean(baseData.loggedInEmail?.data);
        } catch (e) {
            console.error("syncSessionFromServer:", e);
            return false;
        }
    }

    async function handleSignin(email, password, setError) {
        const users = (await axios.get(`${api}users/`, {
            headers: {
                'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY
            }
        })).data.data;

        const user = users.find(user => user.email === email);

        if (!user) {
            setError('No user found with this email');
            return false;
        }

        const data = { password };
        try {
            const response = await axios.post(api + 'users/' + email, data, {
                withCredentials: true,
                headers: {
                    'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY
                }
            });

            // Auth success may surface as the legacy string `'passwords match'`,
            // an object payload, or a 200 with `auth: 'success'`. Treat any of
            // these as success so the FE keeps working through deploy windows
            // where FE/BE are temporarily out of sync.
            const payload = response?.data || {};
            const status = response?.status;
            const dataField = payload.data;
            const isAuthSuccess =
                status >= 200 && status < 300 &&
                (
                    dataField === 'passwords match' ||
                    payload.auth === 'success' ||
                    (dataField && typeof dataField === 'object' && dataField.user) ||
                    payload.user
                );

            if (isAuthSuccess) {
                const sessionOk = await syncSessionFromServer();
                setError('');
                props.setTrigger(false);
                if (typeof props.onSuccess === "function") {
                    props.onSuccess();
                }
                if (!sessionOk) {
                    message.warning(
                        "Login succeeded but the session could not be verified. If you still appear signed out, your browser may be blocking third-party cookies for this site."
                    );
                }
            } else {
                setError('Incorrect password');
            }
        } catch (error) {
            console.error('Error:', error);
            if (error.response && error.response.data.message === 'Invalid password') {
                setError('Incorrect password');
            } else {
                setError('An error occurred. Please try again.');
            }
        }
    }

    // Also add some CSS for the error message styling if you haven't already:


    async function createUser(username, email, password, phone, birthday, email_notifs, text_notifs, gender, setError) {
        const tickets = {}
        try {
            const response = await axios.post(`${api}users/`, null, {
                params: {
                    username,
                    email,
                    password,
                    phone,
                    birthday,
                    email_notifs,
                    text_notifs,
                    gender,
                    tickets
                },
                headers: {
                    'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY
                },
                withCredentials: true // Include cookies with requests
            });
            if (response.data.data === 'email exists') {
                setError('This email already exists.');
            } else {
                const sessionOk = await syncSessionFromServer();
                props.setTrigger(false);
                if (typeof props.onSuccess === "function") {
                    props.onSuccess();
                }
                if (!sessionOk) {
                    message.warning(
                        "Account created, but session could not be verified. Try signing in again."
                    );
                }
            }
            return JSON.stringify(response.data);
        } catch (error) {
            console.error(error);
            throw error; // Handle or rethrow error
        }
    }

    async function handleSignUp(username, newPassword, email, setError) {
        const response = await createUser(username, email, newPassword, "", "", false, false, "", setError)
            .then(async (response) => {
                if (loggedInEmail.data) {
                    const payload = {
                        email: loggedInEmail.data
                    };

                    try {
                        const response = await axios.post(`${api}sendEmail`, payload, {
                            headers: {
                                'Content-Type': 'application/json',
                                'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY
                            }
                        });
                        //console.log('Response:', response.data);
                    } catch (error) {
                        console.error('Error:', error.response ? error.response.data : error.message);
                    }
                }
            })
        //console.log(response);
    }

    async function getRegisteredUserEmails() {
        try {
            const response = await axios.get(`${api}users/`, {
                headers: {
                    'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY
                }
            });
            //console.log("User details:", response.data.data[0].email);
            // console.log(response.data.data)
            return response.data.data.map(obj => obj.email);
        } catch (error) {
            console.error(error);
            throw error; // Handle or rethrow error
        }
    }

    const handleSubmit = (event) => {
        event.preventDefault();

        if (isRegistered) {
            handleSignin(email, liPassword, setError);
        } else {
            // Check if passwords match
            if (newPassword !== confirmPassword) {
                setPasswordMatchError('Passwords do not match');
                return;
            }

            // Reset error messages
            setPasswordMatchError('');
            setEmailError('');

            // Handle sign up
            handleSignUp(fullName, newPassword, email, setEmailError);
        }

    };

    const redirectToExternalBrowser = () => {
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        if (userAgent.match(/Instagram/i)) {
            // TODO: Once the redirect actually works this can be changed to this code instead since we don't need different behavior for Android vs. iPhone
            // We only care if the user is trying to acces the URL directly from Instagram
            // window.location.href = "http://localhost:3000/redirect/";

            if (/android/i.test(userAgent)) {
                window.location.href = "intent://www.ummahfy.com#Intent;scheme=https;package=com.android.chrome;end";
            } else if (/iPad|iPhone/i.test(userAgent)) {
                window.location.href = "http://localhost:3000/redirect/";
            }
        }
    };

    async function handleGoogleLogin() {
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        if (userAgent.match(/Instagram/i)) {
            const currentUrl = encodeURIComponent(window.location.href);
            navigate(`/redirect/?url=${currentUrl}`)
        } else {
            window.location.href = api + "auth/google";
        }
    }

    async function handleGoogleSignup() {
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        if (userAgent.match(/Instagram/i)) {
            const currentUrl = encodeURIComponent(window.location.href);
            navigate(`/redirect/?url=${currentUrl}`)
        } else {
            window.location.href = api + "auth/google";
        }
    }

    const handleClose = () => {
        setError(''); // Clear the error message when closing the component
        props.setTrigger(false);
        setIsRegistered(true);

    };






    const handleEmailChange = (e) => {
        setEmail(e.target.value);
        // Check if the entered email is in the admitted list
        if (!email) {
            setIsRegistered(true);
        }
    };

    const handleEmailBlur = async () => {
        if (!isLoading && email.trim()) {  // Only check if email is not empty
            if (!emails.includes(email)) {
                setIsRegistered(false);
                // Focus on fullNameInputRef if it's not null
                if (fullNameInputRef.current) {
                    fullNameInputRef.current.focus();
                }
            } else {
                setIsRegistered(true);
            }
        }
    };

    const handleForgotPassword = async () => {
        // console.log("calling handleforgotpass")
        try {
            const response = await axios.post(`${api}requestPasswordReset`, {
                email: email,
                accountType: "User",
                requestType: "Forgot Password"
            }, {
                headers: {
                    'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY
                }
            })
            // console.log(response)
        }
        catch (err) {
            console.error(err)
        }
    }

    // const handleEmailBlur = () => {
    //     // Perform the validation when the input loses focus
    //     if (emails.includes(email) || !email) {
    //         setIsRegistered(true);
    //     } else {
    //         setIsRegistered(false);
    //         // Check ref existence before calling focus
    //         if (fullNameInputRef.current) {
    //             fullNameInputRef.current.focus();
    //         }        }
    // };

    // console.log(emails)

    return (
        <>
            <ForgotPassword userType={"User"} trigger={forgotPassword} setTrigger={setForgotPassword} />
            <ConfigProvider
                theme={{
                    components: {
                        Modal: {


                        },
                    },
                }}
            >
                <Modal
                    open={props.trigger && !buttonSignUp}
                    onCancel={handleClose}
                    footer={null}
                    className="signin-modal"
                    wrapClassName="glassmorphic-modal"
                    centered
                    title={
                        <div className="sign-title">
                            {!isRegistered ? (
                                <p>Sign Up to Ummahfy</p>
                            ) : (
                                <p>Sign In or Sign Up</p>
                            )}
                        </div>
                    }
                >
                    <div className="sign-in-text">
                        <p className="signin-intro-copy">
                            Welcome to Ummahfy! A user account allows you to register for events and create free events.
                        </p>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="inputs">
                            <label htmlFor="email">Email</label>
                            <input
                                required
                                type="email"
                                id="email"
                                name="email"
                                className="long-input"
                                value={email}
                                onChange={handleEmailChange}
                                onBlur={handleEmailBlur}
                            />
                        </div>

                        {/* Keep all your existing conditional rendering for sign in/sign up forms */}
                        {isRegistered ? (
                            <>
                                {/* Your existing sign in form content */}
                                <div className="inputs">
                                    <label htmlFor="password">Password</label>
                                    <input
                                        required
                                        type="password"
                                        id="password"
                                        name="password"
                                        className="long-input"
                                        onChange={(e) => setLiPassword(e.target.value)}
                                    />
                                </div>

                                <a className="forgot-password"
                                    onClick={() => {
                                        setForgotPassword(true)
                                        props.setTrigger(false)
                                    }}
                                >
                                    Forgot Password?
                                </a>

                                {error && (
                                    <Alert
                                        message={error}
                                        type="error"
                                        showIcon
                                        style={{
                                            maxHeight: '42px',
                                            marginTop: '8px',
                                            borderRadius: '4px',
                                        }}
                                        closable
                                        onClose={() => setError('')}
                                    />
                                )}

                                <button type="submit" className="signin">Sign In</button>
                                <ConfigProvider
                                    theme={{
                                        components: {
                                            Divider: {
                                                marginLG: 4,
                                            }
                                        }
                                    }}
                                >
                                    <Divider className="or-divider">
                                        <span className="divider-text">OR</span>
                                    </Divider>
                                </ConfigProvider>
                                <button type="button" className="signin google" onClick={handleGoogleLogin}>
                                    <GoogleLogo className="google-logo" />
                                    Continue with Google
                                </button>
                            </>
                        ) : (
                            <>
                                {/* Your existing sign up form content */}
                                <div className="inputs">
                                    <label htmlFor="fullName">Full Name </label>
                                    <input
                                        type="text"
                                        id="fullName"
                                        name="fullName"
                                        value={fullName}
                                        onChange={(e) => setFullName(e.target.value)}
                                        className="long-input"
                                        ref={fullNameInputRef}
                                        required
                                    />
                                </div>

                                {/* ... rest of your sign up form ... */}
                                <div className="inputs">
                                    <label htmlFor="password">Password</label>
                                    <input
                                        type="password"
                                        id="password"
                                        name="password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        className="long-input"
                                        required
                                    />
                                </div>

                                <div className="inputs">
                                    <label htmlFor="confirm">Confirm Password</label>
                                    <input
                                        type="password"
                                        id="confirm"
                                        name="confirm"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className="long-input"
                                        required
                                    />
                                </div>

                                {passwordMatchError && (
                                    <Alert
                                        message={passwordMatchError}
                                        type="error"
                                        showIcon
                                        style={{
                                            maxHeight: '42px',
                                            marginTop: '8px',
                                            borderRadius: '4px',
                                        }}
                                        closable
                                        onClose={() => setPasswordMatchError('')}
                                    />
                                )}
                                {emailError && (
                                    <Alert
                                        message={emailError}
                                        type="error"
                                        showIcon
                                        style={{
                                            maxHeight: '42px',
                                            marginTop: '8px',
                                            borderRadius: '4px',
                                        }}
                                        closable
                                        onClose={() => setEmailError('')}
                                    />
                                )}

                                <button type="submit" className="create-acc">Create Account</button>

                                <Divider className="or-divider">
                                    <span className="divider-text">OR</span>
                                </Divider>

                                <button type="button" className="signin google" onClick={handleGoogleSignup}>
                                    <GoogleLogo className="google-logo" />
                                    Continue with Google
                                </button>
                            </>
                        )}
                    </form>


                </Modal>
            </ConfigProvider>
        </>
    );
}

export default Signin;

/* 
                                    <div className="t-c">
                                        <label htmlFor="terms" className="custom-checkbox terms-cond">
                                            <input type="checkbox" id="terms" name="terms" required />
                                            <span className="checkmark"></span>
                                            <p className="terms">By checking this box, I acknowledge that I have read and agree to abide <br /> by the terms and conditions outlined in the Terms of Service.</p>
                                        </label>
                                    </div> */